/**
 * engine/scratch.ts — the life of the scratch voice: the sound it plays through, the track it
 * plays, and the hand-over of the sound to the element and back.
 *
 * Task 18. `spec.md` «Пластинка»: «Два голоса», «Подготовка трека к скретчу», «Звук скретча»,
 * «Жест, когда скретч готов». The voice's arithmetic is `lib/scratch-dsp.ts`, run in the audio
 * thread by `engine/scratch-worklet.ts`; this module is the page's half, and everything outside
 * the page it needs goes through one seam, {@link ScratchAudio} — «создай контекст, декодируй,
 * загрузи процессор, пошли сообщение процессору, событие от процессора», and the download of the
 * track. `engine/scratch-audio.ts` is the browser behind it; `test/helpers/fake-scratch.ts` the
 * recorder behind it in tests.
 *
 * ## The decks stay the ones that play
 *
 * All the ordinary sound is the decks' (`engine/element.ts`): the background, the lock screen, the
 * streaming. The voice sounds only under a finger and in the return after it, on the deck it
 * belongs to ({@link ScratchDeps.player}'s `elementOf`). While the voice sounds, that deck stands at speed 0
 * without a pause (`engine/speed.ts`), so the button, the lock screen and the watchdog see a held
 * record, as in task 09.
 *
 * ## One context with the decks (task 45)
 *
 * Since task 45 the decks play through Web Audio too, each through a gain of its own, and the
 * context is one for the app (`engine/sound.ts`): {@link ScratchContext.source} is the seam's
 * «источник из элемента». Whoever taps first makes it — the first track played, as a rule, so the
 * screen opening later finds it awake. A context the decks play through is never put to sleep when
 * the screen closes: it would stop the music.
 *
 * ## A voice on each deck (task 57)
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Две равные деки», «Скретч на обеих деках».
 * Each deck has its voice, its track ready and its touch (`Unit`), so a finger scratches either deck
 * while both play. A deck's voice sounds through the door of that deck's chain (`engine/mixer.ts`),
 * not into the speaker: a scratch has the deck's tone and filter and goes through its fader. Each
 * deck's decode keeps to {@link SCRATCH_DECODE_BUDGET}; the two downloads go one at a time, the
 * second waiting for the first — for its decode too, which an abort cannot stop, so two peaks of
 * the decode never meet; a download let go before its decode frees the turn at once. The probe of
 * task 53 held two tracks of 8–10 minutes at once on the owner's iPhone. A deck is ready while
 * anyone holds it (`hold`): the turntable the active deck, the pult both. The
 * rule «во время перехода скретча нет» is gone: the automat is the
 * crossfader's movement, and a finger on either record does not stop it — the hand-over moves the
 * deck without the seek that would end an overlap (`PlayerController.seekDeck`), and the status row
 * still says «ПЕРЕХОД» or «МИКС» (`screens/NowPlaying.tsx`).
 *
 * ## Getting a track ready
 *
 * {@link ScratchController.hold} is called while «Сейчас играет» shows a track. The track is
 * downloaded whole, by the element's own URL (same URL, same server cache), and decoded to one
 * channel at {@link SCRATCH_RATE}, or at the speaker's rate where the phone refuses that. Not at
 * all when the data saver is on, when the catalogue does not know the duration, or when the peak
 * of the decode — rate × 2 channels × 4 bytes × duration — would pass
 * {@link SCRATCH_DECODE_BUDGET}. One download at a time; a new track or the last holder's release
 * cancels it and lets the decoded sound go. A download or a decode that fails leaves the track
 * without a scratch for the rest of the launch: no retry, no message.
 *
 * ## The sound of the scratch
 *
 * An audio context is only let out of its silence by a tap, and a context once woken by a tap
 * is — on the phones this is written for; **not checked** on a phone inside Telegram — woken
 * again without one. So {@link ScratchController.prime} makes it and wakes it inside the tap that
 * opens the screen, and it stays awake while the screen is open, as the prototype's did. Until
 * 2026-09-29 it slept a second after every hand-over and woke at the next touch — and WebKit, the
 * engine of Telegram on an iPhone, then said nothing for a second after the wake (measured over
 * the preview, `.omc/wiki/raw/skretch-kak-v-makete-proverka.md`): the whole of a quick scratch,
 * or of a rewind. Closing the screen puts it to sleep; the tap that opens it again wakes it. The
 * price is a running context, and its battery, while the screen is open.
 * A screen opened from a link had no tap: its first touch is task 09's, and the lift of that
 * finger — a tap in the eyes of the HTML standard, where the landing is not — primes it.
 * Before the context, `navigator.audioSession.type = 'playback'`, where there is one: on an
 * iPhone the silent switch mutes Web Audio otherwise (`engine/sound.ts` does both). No `AudioWorklet`, a context refused, a
 * wake refused, a processor that did not load — each is a refusal, remembered until the app is
 * started again, and every touch after it is task 09's.
 *
 * ## A touch, and the hand-over
 *
 * {@link ScratchController.grab} answers a finger: `undefined` when there is no scratch for this
 * touch (the caller then runs task 09's table), a {@link ScratchHand} when there is. The element
 * goes to speed 0; the voice starts where the element was, at the tempo, and chases the finger —
 * the finger's travel is time by the record's turn: `travel / 360° × turn`. Released, the voice's
 * motor — the prototype's — takes it to the tempo: a quarter of a second from a standstill, 1,75 s
 * from six times backwards, the rewind (`lib/scratch-dsp.ts`, «After the hand»). Then the element,
 * still at 0, is sent to the voice's second plus {@link HANDOFF_LEAD_S}; after its `seeked`, the
 * voice is told where the element waits, and when it gets there the element sounds at the tempo
 * while the voice fades out over 40 ms. A `seeked` that comes after the voice has already passed
 * that second sends the element further on; none within {@link HANDOFF_WAIT_MS} and the element
 * goes on from where it stands. All that while the slider shows the voice's second
 * (`PlayerController.showPosition`).
 *
 * A voice that never answers — a wake the phone neither grants nor refuses — must not keep the
 * element silent for ever: released, the voice is never silent for {@link STEADY_WAIT_MS} before
 * it is at speed, or the sound of the scratch counts as refused and the element comes back by
 * task 09's slope. Each word of the voice starts the wait again: after a rewind the motor alone
 * takes 1,75 s, and a voice that is talking is alive.
 *
 * ## What the record is drawn from
 *
 * While the voice sounds, the record on the screen is its picture, as the prototype's record was
 * the picture of its sound: {@link ScratchController.voiceAt} is where the voice is now — its last
 * `motion`, a frame apart, and its speed since, never guessed further than
 * {@link GUESS_AHEAD_MS} — and `components/Vinyl.tsx` turns the record by it every frame. A record
 * drawn from the finger ran ahead of a voice that chases the finger and is held to six times, and
 * after the lift it turned by a clock of its own: the owner saw the record and the sound part
 * (2026-09-29).
 *
 * ## A touch on a pause
 *
 * A paused deck has its motor off. The voice starts standing, not at the tempo, and released it
 * slows to rest from wherever the hand left it — the prototype's brake, a tempo of 0. Then there
 * is nothing to hand over: the element, paused all along, is sent to the second the record rests
 * at, and «Играть» starts from there. Before 2026-09-29 the motor went to the tempo on a pause
 * too, and every lift played half a second of the song (the owner found it on a phone). Whether
 * the motor is on is asked at the lift: «Играть» pressed under the finger is a record that plays.
 *
 * ## The finger's own clock
 *
 * Each report of the finger goes to the voice with the time since the report before — by the
 * pointer event's own time stamp where the screen has one — and the voice glides to it over that
 * time rather than jumping (`lib/scratch-dsp.ts`, «The finger is followed by its own clock»).
 *
 * The download is the element's own URL (`PlayerController.soundUrlOf`): the player renews a nearly
 * spent link before it plays, and the catalogue's copy of the old one would be refused.
 *
 * ## Loops (task 62)
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Пады»: a loop is the deck's voice going round
 * while the element stands at speed 0 without a pause, as under a hand — a loop without a click is
 * played only from a track whole in memory (`lib/scratch-dsp.ts`, «A loop, a roll, a jump»). Only a
 * deck that plays, its track ready and no hand on its record, loops ({@link ScratchController.loop}):
 * the loop starts where the element is, at the speed the element plays — the deck's tempo, a mix's
 * share, PITCH BEND — not the tempo alone, or a loop pressed in a mix would jump in pitch. Let go
 * ({@link ScratchController.unloop}), the voice plays on — from its place taken into the loop (its
 * last word may be from before it went round), a roll from where the track would be by the page's
 * clock — and the element takes the sound back exactly as after a scratch: sent ahead, waited for,
 * the voice fading as it arrives. A hand on the record, the deck's pause, a new track end it: the
 * pause rests the record where the voice was; a pause that comes with a new track (LOAD pauses the
 * deck first) only lets the loop go — the old loop's second means nothing in the new track. A loop
 * or its hand-over ended any other way — the deck's track let go, a jump of the element
 * ({@link ScratchController.letGo}) — gives the element its speed in one write: it was playing all
 * along, and task 09's slope is a record winding up. BEAT JUMP keeps the loop within the track.
 * Each deck's loop is in `store/scratch.ts` ({@link ScratchDeps.onLoop}); the last one of its track
 * stays for RELOOP ({@link ScratchController.lastLoop}) until the deck takes another track.
 */

import type { DeckSide } from '../lib/pult-layout';
import { TURN_S } from '../lib/records';
import { tempoRate, useDeck } from '../store/deck';
import { markLoop, markReady } from '../store/scratch';
import type { DeckLoop } from '../store/scratch';

import type { MediaElementLike } from './element';
import type { PlayerController } from './player';
import { player } from './player';
import { mayPrefetch } from './prefetch';
import { webScratchAudio } from './scratch-audio';
import type { FromVoice, ToVoice } from './scratch-protocol';
import { createSound, sound as appSound } from './sound';
import type { Sound } from './sound';

/* ────────────────────────────────────────────────────────────────────────────
 * Numbers
 * ──────────────────────────────────────────────────────────────────────────── */

/** The rate the track is decoded at for the scratch: half of 48 000, half the memory. */
export const SCRATCH_RATE = 24_000;

/** The most the decode may take at its peak, in bytes: rate × 2 channels × 4 bytes × seconds. */
export const SCRATCH_DECODE_BUDGET = 120_000_000;

/** Channels and bytes a sample at the decode's peak: the decoder's buffer is stereo float. */
const PEAK_BYTES_PER_FRAME = 2 * 4;

/** The speaker's rate when no context exists yet to ask: the usual one of phones. */
export const DEVICE_RATE_GUESS = 48_000;

/** The element is sent this far ahead of the voice, to have the time to seek. */
export const HANDOFF_LEAD_S = 0.25;

/** Without a `seeked` this long after the seek, the element goes on from where it stands. */
export const HANDOFF_WAIT_MS = 3_000;

/** Released, the voice is never silent this long before it is at speed, or it is a dead one. */
export const STEADY_WAIT_MS = 1_000;

/** Where the voice is now is guessed from its last word and its speed, never further than this. */
export const GUESS_AHEAD_MS = 50;

/* ────────────────────────────────────────────────────────────────────────────
 * The seam
 * ──────────────────────────────────────────────────────────────────────────── */

/** The part of `AudioBuffer` read here. */
export interface DecodedAudio {
  readonly sampleRate: number;
  readonly length: number;
  readonly numberOfChannels: number;
  getChannelData(channel: number): Float32Array;
}

/** The message port of the voice in the audio thread. */
export interface VoicePort {
  post(message: ToVoice, transfer?: Transferable[]): void;
  listen(hear: (message: FromVoice) => void): void;
  /**
   * Task 57: the voice as a node of the context, connected to nothing: the scratch connects it —
   * into its deck's chain, or into the speaker.
   */
  readonly node: SoundNode;
}

/** Task 56: the kinds of node the pult's chains of sound are made of. */
export type SoundNodeKind = 'biquad' | 'delay' | 'gain' | 'convolver' | 'oscillator' | 'analyser';

/** Task 56: a node of the context, by the id the seam gave it — or a deck's element's source. */
export interface SoundNode {
  readonly id: number;
  readonly kind: SoundNodeKind | 'source' | 'voice';
}

/** Task 56: where a node is connected to — another node, one of its parameters, the speaker. */
export type SoundInput =
  SoundNode | { readonly node: SoundNode; readonly param: string } | 'speaker';

/**
 * One audio context: the sound of the scratch, and since task 45 the decks. Since task 56 the
 * decks' chains are built through it a node at a time — «создай узел по имени», «соедини», «задай
 * параметр с плавным подходом», «отключи» (`docs/superpowers/specs/2026-10-05-pult-design.md`,
 * «Шов звука расширяется, не добавляется»), and since task 58 «задай отклик» of a convolver: what is
 * connected to what, and with which numbers, is `engine/mixer.ts`'s, `lib/pult-mixer.ts`'s and
 * `lib/pult-effects.ts`'s, not the browser's.
 */
export interface ScratchContext {
  /** The speaker's samples a second. */
  readonly sampleRate: number;
  /** `'suspended'`, `'running'`, `'closed'`, or iOS's `'interrupted'`. */
  state(): string;
  resume(): Promise<void>;
  suspend(): Promise<void>;
  /** Loads the processor file and makes a voice — connected to nothing yet (task 57). */
  voice(): Promise<VoicePort>;
  /**
   * Task 45: a deck's element into the context — its source. For good: an element cannot be taken
   * out again. Throws when the browser refuses.
   */
  source(el: MediaElementLike): SoundNode;
  /** Task 56: a fresh node of `kind`, connected to nothing. */
  node(kind: SoundNodeKind): SoundNode;
  connect(from: SoundNode, to: SoundInput): void;
  /**
   * Task 56: `name` of `node` — a number glides to `value` with the time constant `glideS` (none
   * or 0: at once); a string (a filter's `type`) is set as it is.
   */
  set(node: SoundNode, name: string, value: number | string, glideS?: number): void;
  /**
   * Task 58: a convolver's response — `channels` of samples at {@link ScratchContext.sampleRate},
   * computed by the page (`lib/pult-effects.ts`), not fetched.
   */
  response(node: SoundNode, channels: readonly Float32Array[]): void;
  disconnect(node: SoundNode): void;
  /** Task 56: an analyser's level now, 0…1 — the RMS of the last samples it saw. */
  level(node: SoundNode): number;
}

export interface ScratchAudio {
  /** An audio context, `AudioWorklet` and an offline context all exist. */
  readonly supported: boolean;
  /** `navigator.audioSession.type = 'playback'`, where there is one. Never throws. */
  preferPlayback(): void;
  /** Throws when the browser refuses one. */
  createContext(): ScratchContext;
  /** A decode at `rate` samples a second is accepted. */
  decodesAt(rate: number): boolean;
  decode(data: ArrayBuffer, rate: number): Promise<DecodedAudio>;
  /** The whole file. Rejects on a failed answer and on `signal`. */
  fetch(url: string, signal: AbortSignal): Promise<ArrayBuffer>;
}

/** What a track to get ready is, to this module. */
export interface ScratchTrack {
  readonly id: number;
  readonly durationS: number;
  readonly stream: { readonly url: string };
}

export interface ScratchDeps {
  readonly audio: ScratchAudio;
  /**
   * Task 45: the app's one context (`engine/sound.ts`), shared with the decks. By default one of
   * the scratch's own, made from {@link audio}.
   */
  readonly sound?: Sound | undefined;
  /**
   * The player: each deck's element — its position, and its `seeked`; its speed, which is the
   * player's to write; the door of its chain — and which deck is active (task 57).
   */
  readonly player: () => Pick<
    PlayerController,
    | 'showPosition'
    | 'activeSide'
    | 'elementOf'
    | 'speedOf'
    | 'seekDeck'
    | 'soundUrlOf'
    | 'heldAtOf'
    | 'headOf'
    | 'whereDeck'
  >;
  /**
   * One turn of the record on the deck, in seconds. By default the record the deck carries
   * (`store/deck.ts`, task 21).
   */
  readonly turnS?: () => number;
  /** The speed of the record nobody touches. By default the deck's tempo (task 23). */
  readonly tempo?: () => number;
  /** The data saver allows a download. */
  readonly mayDownload: () => boolean;
  /** Milliseconds: the clock for a finger that brings no time stamp. By default `performance`. */
  readonly now?: () => number;
  /** Whether a finger now would get the scratch on deck `side` — «СКРЕТЧ» is lit for it. */
  readonly onReady: (ready: boolean, side: DeckSide) => void;
  /** Task 62: deck `side`'s loop now, or none — see «Loops». */
  readonly onLoop: (loop: DeckLoop | undefined, side: DeckSide) => void;
}

/** Task 62: a loop the pult asks for — how long, in seconds and in the beats it names; a roll? */
export interface LoopAsked {
  readonly lengthS: number;
  readonly beats: number;
  readonly roll: boolean;
}

/** A finger on the record, while the scratch has it. */
export interface ScratchHand {
  /**
   * The finger has turned the record `travelDeg` degrees since it landed, clockwise, at `atMs` —
   * the move's time stamp, on the clock the landing's was on. By default now.
   */
  turn(travelDeg: number, atMs?: number): void;
  /** The finger left, by any way out: the voice returns and hands the sound back. */
  release(): void;
}

/**
 * Task 57: every call names a deck — or, naming none, the active one: the turntable of «Сейчас
 * играет» shows that deck and never says which.
 */
export interface ScratchController {
  /** Inside the tap that opens «Сейчас играет», or the lift of a first finger. Idempotent. */
  prime(): void;
  /**
   * `track` is to be ready on deck `side`: «Сейчас играет» shows it, or the pult is open (task 57).
   * The release lets it go; a deck is ready while anyone holds it, with its latest holder's track,
   * and a track held already is not got ready again.
   */
  hold(track: ScratchTrack, side: DeckSide): () => void;
  /** A finger landed at `atMs`, by default now. `undefined`: this touch is task 09's. */
  grab(atMs?: number, side?: DeckSide): ScratchHand | undefined;
  /**
   * Where the voice is at `nowMs` (by default now), in seconds of the track — what the record is
   * drawn from. `undefined` while the element has the sound.
   */
  voiceAt(nowMs?: number, side?: DeckSide): number | undefined;
  /** Whatever the voice does ends now, and the element takes the sound back by its slope. */
  abort(side?: DeckSide): void;
  /**
   * Task 62: deck `side`'s voice loops `asked` from where the deck is — see «Loops». The loop
   * placed, or `undefined` when the deck cannot: no voice ready for its track, a hand on its
   * record, the deck not playing.
   */
  loop(side: DeckSide, asked: LoopAsked): DeckLoop | undefined;
  /** Task 62: RELOOP — `loop` again, the voice from its start. `false` when the deck cannot. */
  loopAt(side: DeckSide, loop: DeckLoop): boolean;
  /** Task 62: the looping deck's loop `asked` long from its start — ½, 2×, another pad. */
  resize(side: DeckSide, asked: Omit<LoopAsked, 'roll'>): void;
  /** Task 62: the loop let go; the element takes the sound back where the voice plays on. */
  unloop(side: DeckSide): void;
  /** Task 62: BEAT JUMP of a looping deck — the voice and its loop. `false`: it does not loop. */
  jumpLoop(side: DeckSide, byS: number): boolean;
  /**
   * Task 62: a loop, or the hand-over after one or after a scratch, ends now, and the element plays
   * on from where it stands at its speed — before the pult moves the element. A hand on the
   * record is left alone.
   */
  letGo(side: DeckSide): void;
  /** Task 62: the deck's last loop on the track it holds — what RELOOP plays again. */
  lastLoop(side: DeckSide): DeckLoop | undefined;
  dispose(): void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The controller
 * ──────────────────────────────────────────────────────────────────────────── */

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'held' }
  | {
      readonly kind: 'returning';
      readonly deadline: ReturnType<typeof setTimeout>;
      /** The deck's motor was off at the lift: the record comes to rest, nothing is handed over. */
      readonly toRest: boolean;
      /** The speed the motor takes the voice to: the tempo, or 0 on a pause. */
      readonly tempo: number;
    }
  | { readonly kind: 'handing'; readonly deadline: ReturnType<typeof setTimeout> }
  /** Task 62: the voice goes round a loop — see «Loops». */
  | { readonly kind: 'looping' };

/** Task 57: one deck's voice, its track and its touch. */
interface Unit {
  readonly side: DeckSide;
  port: VoicePort | undefined;
  /** The voice is being made. */
  making: boolean;
  /** Where the voice is connected to now. */
  routedTo: SoundInput | undefined;
  /** Who wants a track ready on this deck, and which. */
  readonly holders: Map<symbol, ScratchTrack>;
  /** The track this deck is to have ready. */
  attachedId: number | undefined;
  /** The download and decode in flight. */
  preparing: AbortController | undefined;
  /** A download waiting for the other deck's to finish. */
  waiting: ScratchTrack | undefined;
  /** Decoded, for the attached track: the samples wait here until there is a voice to own them. */
  ready:
    { readonly id: number; samples: Float32Array | undefined; readonly rate: number } | undefined;
  phase: Phase;
  /** The voice's second, as it last said it. */
  voiceS: number;
  /** The voice's last word of where it is and how fast, and when the page heard it. */
  heard: { atS: number; rate: number; atMs: number };
  seekedListener: ((event: Event) => void) | undefined;
  /** The element {@link Unit.seekedListener} listens on. */
  seekedOn: MediaElementLike | undefined;
  /** Where the element was sent to wait for the voice. */
  handoffAtS: number;
  lit: boolean;
  /** Task 62: the loop the voice plays now. */
  loop: DeckLoop | undefined;
  /** Task 62: the last loop on the deck's track, for RELOOP. */
  lastLoop: DeckLoop | undefined;
  /** Task 62: a roll's start — where and when, at what speed — the track goes on from there. */
  rollFrom: { readonly atS: number; readonly atMs: number; readonly rate: number } | undefined;
  /** Task 62: the deck's pause, listened for while it loops. */
  pausedListener: ((event: Event) => void) | undefined;
  pausedOn: MediaElementLike | undefined;
}

/** The track's channels as one: the average, into a fresh array the voice can own. */
export function downmix(decoded: DecodedAudio): Float32Array {
  const channels = Math.max(1, decoded.numberOfChannels);
  const mono = new Float32Array(decoded.length);
  for (let c = 0; c < channels; c += 1) {
    const data = decoded.getChannelData(c);
    for (let i = 0; i < mono.length; i += 1) mono[i] = (mono[i] ?? 0) + (data[i] ?? 0) / channels;
  }
  return mono;
}

export function createScratch(deps: ScratchDeps): ScratchController {
  const { audio: sound } = deps;
  const shared = deps.sound ?? createSound(sound);
  const now = deps.now ?? ((): number => performance.now());

  let refused = false;
  let context: ScratchContext | undefined;
  const failed = new Set<number>();
  /**
   * Task 57: the one download at a time, and whether it has come to its decode — which an abort
   * cannot stop, so the next download waits for it to end even after the abort.
   */
  let downloading: { readonly job: AbortController; decoding: boolean } | undefined;

  function newUnit(side: DeckSide): Unit {
    return {
      side,
      port: undefined,
      making: false,
      routedTo: undefined,
      holders: new Map(),
      attachedId: undefined,
      preparing: undefined,
      waiting: undefined,
      ready: undefined,
      phase: { kind: 'idle' },
      voiceS: 0,
      heard: { atS: 0, rate: 0, atMs: 0 },
      seekedListener: undefined,
      seekedOn: undefined,
      handoffAtS: 0,
      lit: false,
      loop: undefined,
      lastLoop: undefined,
      rollFrom: undefined,
      pausedListener: undefined,
      pausedOn: undefined,
    };
  }

  const units: Readonly<Record<DeckSide, Unit>> = { A: newUnit('A'), B: newUnit('B') };

  /** A side named, or the deck that is active. */
  function unitOf(side: DeckSide | undefined): Unit {
    return units[side ?? deps.player().activeSide()];
  }

  /* ── one deck's voice ──────────────────────────────────────────────────── */

  /** The deck's element: the one the voice takes the sound from and gives it back to. */
  function elementOf(unit: Unit): MediaElementLike | undefined {
    return deps.player().elementOf(unit.side);
  }

  function turnOf(unit: Unit): number {
    return deps.turnS?.() ?? TURN_S[useDeck.getState().decks[unit.side].size];
  }

  function tempoOf(unit: Unit): number {
    return deps.tempo?.() ?? tempoRate(useDeck.getState().decks[unit.side].pitch);
  }

  function speedOf(unit: Unit): PlayerController['speed'] | undefined {
    return deps.player().speedOf(unit.side);
  }

  /** The slider shows a hand's second for the active deck only: it is the deck it shows. */
  function show(unit: Unit, atS: number | undefined): void {
    const player = deps.player();
    if (player.activeSide() === unit.side) player.showPosition(atS);
  }

  /**
   * «СКРЕТЧ» is lit when a finger landing now would get the scratch: the track ready, and a voice
   * to play it. A screen opened from a link has no voice until its first lift, and a mark lit
   * over a touch that would still be task 09's promised what it could not give (owner's choice,
   * 2026-09-29). Task 57: each deck its own (`store/scratch.ts`, `byDeck`).
   */
  function light(unit: Unit): void {
    const lit =
      !refused &&
      unit.ready !== undefined &&
      unit.ready.id === unit.attachedId &&
      unit.port !== undefined;
    if (lit === unit.lit) return;
    unit.lit = lit;
    deps.onReady(lit, unit.side);
  }

  function say(unit: Unit, message: ToVoice, transfer?: Transferable[]): void {
    unit.port?.post(message, transfer);
  }

  /** Hands the waiting samples to the deck's voice, once there is one. */
  function handOver(unit: Unit): void {
    const samples = unit.ready?.samples;
    if (unit.port === undefined || unit.ready === undefined || samples === undefined) return;
    say(unit, { type: 'load', samples, rate: unit.ready.rate }, [samples.buffer]);
    unit.ready.samples = undefined;
  }

  /**
   * Task 57: the deck's voice sounds through the door of the deck's chain — before LOW, so a
   * scratch has the deck's tone, filter and fader — or, while the decks are not in the context,
   * straight into the speaker, as before task 57. Looked at again at every touch.
   */
  function route(unit: Unit): void {
    const made = context;
    const voice = unit.port;
    if (made === undefined || voice === undefined) return;
    const to: SoundInput = deps.player().headOf(unit.side) ?? 'speaker';
    if (unit.routedTo === to) return;
    if (unit.routedTo !== undefined) made.disconnect(voice.node);
    made.connect(voice.node, to);
    unit.routedTo = to;
  }

  /** The deck's voice, made once there is a context and the deck has a track to be ready. */
  function makeVoice(unit: Unit): void {
    const made = context;
    if (made === undefined || refused || unit.port !== undefined || unit.making) return;
    unit.making = true;
    void made
      .voice()
      .then((voicePort) => {
        unit.making = false;
        if (refused) return;
        voicePort.listen((message) => {
          hear(unit, message);
        });
        unit.port = voicePort;
        route(unit);
        handOver(unit);
        light(unit);
      })
      .catch(() => {
        unit.making = false;
        refuse();
      });
  }

  /** Stops every wait of the phase: its deadline, the element's `seeked`, a loop's pause. */
  function stopWaiting(unit: Unit): void {
    if (unit.seekedListener !== undefined) {
      unit.seekedOn?.removeEventListener('seeked', unit.seekedListener);
    }
    unit.seekedListener = undefined;
    unit.seekedOn = undefined;
    if (unit.pausedListener !== undefined) {
      unit.pausedOn?.removeEventListener('pause', unit.pausedListener);
    }
    unit.pausedListener = undefined;
    unit.pausedOn = undefined;
    const phase = unit.phase;
    if (phase.kind === 'returning' || phase.kind === 'handing') clearTimeout(phase.deadline);
  }

  /**
   * Task 62: the deck loops `loop` now, or nothing — the slice is told when it changes; a loop that
   * is not a roll is the one RELOOP plays again.
   */
  function setLoop(unit: Unit, loop: DeckLoop | undefined): void {
    if (loop === undefined) unit.rollFrom = undefined;
    else if (!loop.roll) unit.lastLoop = loop;
    if (unit.loop === loop) return;
    unit.loop = loop;
    deps.onLoop(loop, unit.side);
  }

  /**
   * The element has the sound again: `now` — at the tempo in one write, where the voice fades
   * out (the hand-over); `slope` — by task 09's slope, from where it stands (everything else).
   */
  function handBack(unit: Unit, how: 'now' | 'slope'): void {
    stopWaiting(unit);
    setLoop(unit, undefined);
    unit.phase = { kind: 'idle' };
    const speed = speedOf(unit);
    if (how === 'now') speed?.releaseNow();
    else speed?.release();
    show(unit, undefined);
  }

  function finish(unit: Unit): void {
    handBack(unit, 'now');
  }

  function abortUnit(unit: Unit): void {
    const kind = unit.phase.kind;
    if (kind === 'idle') return;
    say(unit, { type: 'fade' });
    // Task 62: a loop's element was playing all along — see «Loops».
    handBack(unit, kind === 'looping' || kind === 'handing' ? 'now' : 'slope');
  }

  function refuse(): void {
    if (refused) return;
    refused = true;
    for (const unit of Object.values(units)) {
      abortUnit(unit);
      light(unit);
    }
  }

  /**
   * The record rests at `atS` on a paused deck: the element, still paused, waits there, and the
   * voice — standing, so silent — steps aside.
   */
  function comeToRest(unit: Unit, atS: number): void {
    stopWaiting(unit);
    deps.player().seekDeck(unit.side, atS);
    say(unit, { type: 'fade' });
    finish(unit);
  }

  /** Sends the element ahead of the voice, and waits for it there. */
  function seekAhead(unit: Unit, fromS: number): void {
    unit.handoffAtS = Math.max(0, fromS + HANDOFF_LEAD_S);
    deps.player().seekDeck(unit.side, unit.handoffAtS);
  }

  function beginHandoff(unit: Unit, atS: number): void {
    stopWaiting(unit);
    const el = elementOf(unit);
    if (el === undefined) {
      finish(unit);
      return;
    }
    const deadline = setTimeout(() => {
      // No `seeked`: the element goes on from where it stands, and the voice steps aside.
      say(unit, { type: 'fade' });
      finish(unit);
    }, HANDOFF_WAIT_MS);
    unit.phase = { kind: 'handing', deadline };
    const listener = (): void => {
      const standsAtS = Number.isFinite(el.currentTime) ? el.currentTime : unit.handoffAtS;
      if (unit.voiceS < standsAtS) {
        say(unit, { type: 'arrive', atS: standsAtS });
        return;
      }
      // The voice is already past it: further on, within the same three seconds — unless the
      // element cannot go further (the end of the track, the end of what is loaded), and then
      // it takes the sound from where it is.
      seekAhead(unit, unit.voiceS);
      if (!(el.currentTime > standsAtS)) {
        say(unit, { type: 'fade' });
        finish(unit);
      }
    };
    unit.seekedListener = listener;
    el.addEventListener('seeked', listener);
    unit.seekedOn = el;
    seekAhead(unit, atS);
  }

  /** A word from a returning voice: it is alive, and the wait for it starts again. */
  function keepAlive(unit: Unit): void {
    const phase = unit.phase;
    if (phase.kind !== 'returning') return;
    clearTimeout(phase.deadline);
    unit.phase = { ...phase, deadline: setTimeout(refuse, STEADY_WAIT_MS) };
  }

  function hear(unit: Unit, message: FromVoice): void {
    const phase = unit.phase;
    if (phase.kind === 'idle') return;
    switch (message.type) {
      case 'motion':
        unit.voiceS = message.atS;
        unit.heard = { atS: message.atS, rate: message.rate, atMs: now() };
        keepAlive(unit);
        return;
      case 'position':
        unit.voiceS = message.atS;
        unit.heard = { atS: message.atS, rate: unit.heard.rate, atMs: now() };
        show(unit, message.atS);
        keepAlive(unit);
        return;
      case 'steady':
        unit.voiceS = message.atS;
        if (phase.kind !== 'returning') return;
        unit.heard = { atS: message.atS, rate: phase.tempo, atMs: now() };
        if (phase.toRest) comeToRest(unit, message.atS);
        else beginHandoff(unit, message.atS);
        return;
      case 'arrived':
        if (phase.kind === 'handing') finish(unit);
        return;
      case 'silent':
        return;
    }
  }

  /**
   * Where the track is, as a second: the element's — or, for a track held at launch that the
   * element has not been given, the second it waits at, not the element's zero.
   */
  function elementS(unit: Unit): number {
    const held = deps.player().heldAtOf(unit.side);
    if (held !== undefined) return held;
    const el = elementOf(unit);
    return el !== undefined && Number.isFinite(el.currentTime) && el.currentTime > 0
      ? el.currentTime
      : 0;
  }

  function prime(): void {
    if (refused) return;
    if (context !== undefined) {
      // The tap that opens the screen again: the sound of the scratch wakes inside it.
      const state = context.state();
      if (state !== 'running' && state !== 'closed') void context.resume().catch(refuse);
      return;
    }
    if (!sound.supported) {
      refuse();
      return;
    }
    // The decks' first tap may have made it already; then it is only found, not made again.
    const made = shared.wake();
    if (made === undefined) {
      refuse();
      return;
    }
    context = made;
    // Inside the tap: the wake that unlocks it, and it stays awake while the screen is open.
    void made.resume().catch(refuse);
    // The active deck's voice at once, as before task 57; another deck's when it has a track.
    const first = unitOf(undefined);
    makeVoice(first);
    for (const unit of Object.values(units)) {
      if (unit !== first && unit.attachedId !== undefined) makeVoice(unit);
    }
  }

  /* ── getting a deck's track ready ──────────────────────────────────────── */

  function letGoOfTrack(unit: Unit): void {
    // A download let go frees the turn at once; a decode keeps it until it ends (`downloading`).
    if (
      unit.preparing !== undefined &&
      downloading?.job === unit.preparing &&
      !downloading.decoding
    ) {
      downloading = undefined;
    }
    unit.preparing?.abort();
    unit.preparing = undefined;
    unit.waiting = undefined;
    abortUnit(unit);
    if (unit.ready !== undefined && unit.ready.samples === undefined) say(unit, { type: 'unload' });
    unit.ready = undefined;
  }

  function decodeRate(): number {
    if (sound.decodesAt(SCRATCH_RATE)) return SCRATCH_RATE;
    return context?.sampleRate ?? DEVICE_RATE_GUESS;
  }

  /** The track the deck's holders want, the latest one's. */
  function wanted(unit: Unit): ScratchTrack | undefined {
    return [...unit.holders.values()].at(-1);
  }

  /** The deck's track made ready — after the other deck's download, one at a time (task 57). */
  function prepare(unit: Unit, track: ScratchTrack): void {
    if (downloading !== undefined) {
      unit.waiting = track;
      return;
    }
    unit.waiting = undefined;
    const rate = decodeRate();
    const job = new AbortController();
    unit.preparing = job;
    const turn = { job, decoding: false };
    downloading = turn;
    const forId = track.id;
    void (async () => {
      try {
        const url = deps.player().soundUrlOf(unit.side) ?? track.stream.url;
        const bytes = await sound.fetch(url, job.signal);
        if (job.signal.aborted) return;
        turn.decoding = true;
        const decoded = await sound.decode(bytes, rate);
        if (job.signal.aborted) return;
        unit.ready = { id: forId, samples: downmix(decoded), rate: decoded.sampleRate };
        handOver(unit);
        light(unit);
      } catch {
        if (!job.signal.aborted) failed.add(forId);
      } finally {
        if (unit.preparing === job) unit.preparing = undefined;
        if (downloading === turn) downloading = undefined;
        startWaiting();
      }
    })();
  }

  /** The download that waited for the other deck's to finish. */
  function startWaiting(): void {
    if (downloading !== undefined) return;
    for (const unit of Object.values(units)) {
      const track = unit.waiting;
      if (track !== undefined && track.id === unit.attachedId) {
        prepare(unit, track);
        return;
      }
    }
  }

  /** The deck's attached track is now `track`, or nothing. */
  function attachTo(unit: Unit, track: ScratchTrack | undefined): void {
    if (unit.attachedId === track?.id) return;
    letGoOfTrack(unit);
    unit.attachedId = track?.id;
    unit.lastLoop = undefined;
    light(unit);
    if (track === undefined) {
      startWaiting();
      // A context the decks play through is never put to sleep: it would stop the music.
      const idle = Object.values(units).every(
        (one) => one.attachedId === undefined && one.phase.kind === 'idle',
      );
      if (idle && !shared.carriesDecks && context?.state() === 'running') {
        void context.suspend().catch(refuse);
      }
      return;
    }
    startWaiting();
    if (refused || !sound.supported || failed.has(track.id) || !deps.mayDownload()) return;
    if (!(track.durationS > 0)) return;
    if (decodeRate() * PEAK_BYTES_PER_FRAME * track.durationS > SCRATCH_DECODE_BUDGET) return;
    makeVoice(unit);
    prepare(unit, track);
  }

  function hold(track: ScratchTrack, side: DeckSide): () => void {
    const unit = units[side];
    const owner = Symbol('holder');
    unit.holders.set(owner, track);
    attachTo(unit, wanted(unit));
    return () => {
      if (!unit.holders.delete(owner)) return;
      attachTo(unit, wanted(unit));
    };
  }

  /* ── a finger ──────────────────────────────────────────────────────────── */

  function grab(atMs?: number, side?: DeckSide): ScratchHand | undefined {
    const unit = unitOf(side);
    const made = context;
    if (refused || made === undefined || unit.port === undefined) return undefined;
    const ready = unit.ready;
    if (ready === undefined || ready.id !== unit.attachedId || made.state() === 'closed') {
      return undefined;
    }
    const el = elementOf(unit);
    const speed = speedOf(unit);
    if (el === undefined || speed === undefined) return undefined;
    // Task 57: a transition no longer keeps the finger off — the automat is the crossfader's.
    // Awake already while the screen is open; iOS's `interrupted` — a call, the background — is not.
    void made.resume().catch(refuse);
    route(unit);

    const fromSilence = unit.phase.kind === 'idle';
    stopWaiting(unit);
    // Task 62: the finger has the record — the voice lets its loop go (`lib/scratch-dsp.ts`).
    setLoop(unit, undefined);
    unit.phase = { kind: 'held' };
    speed.hush();
    if (fromSilence) {
      unit.voiceS = elementS(unit);
      // A paused deck's record stands under the hand; a playing one's is taken at its speed.
      const rate = el.paused ? 0 : tempoOf(unit);
      unit.heard = { atS: unit.voiceS, rate, atMs: atMs ?? now() };
      say(unit, { type: 'hold', atS: unit.voiceS, rate });
      show(unit, unit.voiceS);
    } else {
      // A new touch while the voice still sounds takes it where it is.
      say(unit, { type: 'hold' });
    }

    let held = true;
    let reportedAtMs = atMs ?? now();
    return {
      turn(travelDeg: number, movedAtMs?: number): void {
        if (!held || unit.phase.kind !== 'held') return;
        const at = movedAtMs ?? now();
        const spanS = Math.max(0, (at - reportedAtMs) / 1000);
        reportedAtMs = at;
        say(unit, { type: 'follow', travelS: (travelDeg / 360) * turnOf(unit), spanS });
      },
      release(): void {
        if (!held) return;
        held = false;
        if (unit.phase.kind !== 'held') return;
        // A voice that never says it is at speed is a sound of the scratch that does not work.
        const toRest = el.paused;
        const to = toRest ? 0 : tempoOf(unit);
        unit.phase = {
          kind: 'returning',
          deadline: setTimeout(refuse, STEADY_WAIT_MS),
          toRest,
          tempo: to,
        };
        say(unit, { type: 'release', tempo: to });
      },
    };
  }

  /* ── loops (task 62) ───────────────────────────────────────────────────── */

  /** Where the voice is now, by its last word and its speed since — never guessed far. */
  function voiceNow(unit: Unit): number {
    const aheadMs = Math.min(GUESS_AHEAD_MS, Math.max(0, now() - unit.heard.atMs));
    return unit.heard.atS + (unit.heard.rate * aheadMs) / 1000;
  }

  /** `atS` taken into `loop`: a place past its end has gone round. */
  function intoLoop(atS: number, loop: DeckLoop): number {
    const lap = loop.endS - loop.startS;
    if (!(lap > 0)) return atS;
    const into = (atS - loop.startS) % lap;
    return loop.startS + (into < 0 ? into + lap : into);
  }

  /** The deck's element, when the deck may loop now — see «Loops». */
  function elementForLoop(unit: Unit): MediaElementLike | undefined {
    const made = context;
    if (refused || made === undefined || unit.port === undefined || made.state() === 'closed') {
      return undefined;
    }
    const ready = unit.ready;
    if (ready === undefined || ready.id !== unit.attachedId || unit.phase.kind === 'held') {
      return undefined;
    }
    const el = elementOf(unit);
    if (el === undefined || el.paused || speedOf(unit) === undefined) return undefined;
    return el;
  }

  /** The deck stopped under its loop — see «Loops». */
  function listenForPause(unit: Unit, el: MediaElementLike): void {
    const listener = (): void => {
      if (unit.phase.kind !== 'looping') return;
      if (deps.player().whereDeck(unit.side)?.trackId !== unit.attachedId) {
        abortUnit(unit);
        return;
      }
      comeToRest(unit, unit.voiceS);
    };
    unit.pausedListener = listener;
    unit.pausedOn = el;
    el.addEventListener('pause', listener);
  }

  /** The speed the element plays at: the deck's tempo, a mix's share, PITCH BEND. */
  function deckRate(unit: Unit): number {
    return speedOf(unit)?.base ?? tempoOf(unit);
  }

  /** Tells the voice `loop`, at `rate` — from `atS`, or where it is. */
  function tellLoop(unit: Unit, loop: DeckLoop, rate: number, atS?: number): void {
    say(unit, {
      type: 'loop',
      startS: loop.startS,
      endS: loop.endS,
      rate,
      ...(atS === undefined ? {} : { atS }),
      roll: loop.roll,
    });
  }

  /** The voice goes round `loop` — from `atS`, or where it is; the element stands at 0. */
  function startLoop(unit: Unit, el: MediaElementLike, loop: DeckLoop, atS?: number): void {
    const rate = deckRate(unit);
    stopWaiting(unit);
    void context?.resume().catch(refuse);
    route(unit);
    speedOf(unit)?.hush();
    unit.phase = { kind: 'looping' };
    if (atS !== undefined) {
      unit.voiceS = atS;
      unit.heard = { atS, rate, atMs: now() };
      show(unit, atS);
    }
    tellLoop(unit, loop, rate, atS);
    listenForPause(unit, el);
    setLoop(unit, loop);
  }

  return {
    prime,
    hold,
    grab,
    abort(side?: DeckSide): void {
      abortUnit(unitOf(side));
    },
    loop(side: DeckSide, asked: LoopAsked): DeckLoop | undefined {
      const unit = units[side];
      const el = elementForLoop(unit);
      if (el === undefined || !(asked.lengthS > 0)) return undefined;
      const fromSilence = unit.phase.kind === 'idle';
      const startS = fromSilence ? elementS(unit) : voiceNow(unit);
      const placed: DeckLoop = {
        startS,
        endS: startS + asked.lengthS,
        beats: asked.beats,
        roll: asked.roll,
      };
      const rollFrom = asked.roll ? { atS: startS, atMs: now(), rate: deckRate(unit) } : undefined;
      startLoop(unit, el, placed, fromSilence ? startS : undefined);
      unit.rollFrom = rollFrom;
      return placed;
    },
    loopAt(side: DeckSide, loop: DeckLoop): boolean {
      const unit = units[side];
      const el = elementForLoop(unit);
      if (el === undefined) return false;
      unit.rollFrom = undefined;
      startLoop(unit, el, { ...loop, roll: false }, loop.startS);
      return true;
    },
    resize(side: DeckSide, asked: Omit<LoopAsked, 'roll'>): void {
      const unit = units[side];
      const loop = unit.loop;
      if (unit.phase.kind !== 'looping' || loop === undefined || !(asked.lengthS > 0)) return;
      const next: DeckLoop = { ...loop, endS: loop.startS + asked.lengthS, beats: asked.beats };
      tellLoop(unit, next, deckRate(unit));
      setLoop(unit, next);
    },
    unloop(side: DeckSide): void {
      const unit = units[side];
      const loop = unit.loop;
      if (unit.phase.kind !== 'looping' || loop === undefined) return;
      const roll = unit.rollFrom;
      say(unit, { type: 'unloop' });
      // The voice plays on — from its place taken into the loop, a roll from where the track would be.
      const fromS =
        roll === undefined
          ? intoLoop(voiceNow(unit), loop)
          : roll.atS + ((now() - roll.atMs) / 1000) * roll.rate;
      unit.voiceS = fromS;
      unit.heard = { atS: fromS, rate: unit.heard.rate, atMs: now() };
      setLoop(unit, undefined);
      beginHandoff(unit, fromS);
    },
    jumpLoop(side: DeckSide, byS: number): boolean {
      const unit = units[side];
      const loop = unit.loop;
      if (unit.phase.kind !== 'looping' || loop === undefined || !Number.isFinite(byS)) {
        return false;
      }
      // The loop stays within the track: a jump past an end stops at it.
      const durationS = wanted(unit)?.durationS ?? Number.POSITIVE_INFINITY;
      const by = Math.min(durationS - loop.endS, Math.max(-loop.startS, byS));
      say(unit, { type: 'jump', byS: by });
      const roll = unit.rollFrom;
      setLoop(unit, { ...loop, startS: loop.startS + by, endS: loop.endS + by });
      unit.rollFrom = roll === undefined ? undefined : { ...roll, atS: roll.atS + by };
      unit.voiceS += by;
      unit.heard = { ...unit.heard, atS: unit.heard.atS + by };
      return true;
    },
    letGo(side: DeckSide): void {
      const unit = units[side];
      const kind = unit.phase.kind;
      if (kind !== 'looping' && kind !== 'handing') return;
      say(unit, { type: 'fade' });
      handBack(unit, 'now');
    },
    lastLoop(side: DeckSide): DeckLoop | undefined {
      return units[side].lastLoop;
    },
    voiceAt(nowMs?: number, side?: DeckSide): number | undefined {
      const unit = unitOf(side);
      if (unit.phase.kind === 'idle') return undefined;
      const aheadMs = Math.min(GUESS_AHEAD_MS, Math.max(0, (nowMs ?? now()) - unit.heard.atMs));
      return unit.heard.atS + (unit.heard.rate * aheadMs) / 1000;
    },
    dispose(): void {
      for (const unit of Object.values(units)) {
        unit.holders.clear();
        attachTo(unit, undefined);
        if (unit.port !== undefined && unit.routedTo !== undefined)
          context?.disconnect(unit.port.node);
        unit.routedTo = undefined;
      }
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 * ──────────────────────────────────────────────────────────────────────────── */

let instance: ScratchController | undefined;

/** The controller, created on first use with the browser's Web Audio and the app's context. */
export function scratch(): ScratchController {
  instance ??= createScratch({
    audio: webScratchAudio(),
    sound: appSound(),
    player,
    mayDownload: mayPrefetch,
    onReady: markReady,
    onLoop: markLoop,
  });
  return instance;
}

/** Replaces the controller — the web suite's seam, like `installPlayer`. */
export function installScratch(deps: Omit<ScratchDeps, 'onReady' | 'onLoop'>): ScratchController {
  instance?.dispose();
  markReady(false, 'A');
  markReady(false, 'B');
  markLoop(undefined, 'A');
  markLoop(undefined, 'B');
  instance = createScratch({
    ...deps,
    onReady: markReady,
    onLoop: markLoop,
  });
  return instance;
}
